import { Injectable } from '@angular/core';
import { isNativePlatform, isPluginAvailable } from '@vevents/native-services';

/**
 * MOB-100 Stage 18 — biometric (Face ID / fingerprint) wrapper used by the
 * sudo prompt's optional biometric branch.
 *
 * Per the prompt's architectural-guidance: *"Sudo prompt: biometric
 * (Face ID / fingerprint) supported via @capacitor-community/biometric-auth
 * or equivalent. Fallback to password if biometric not set up or
 * declined."*
 *
 * The plugin is resolved lazily so apps that omit the dependency keep
 * building (the platform service falls back to "not supported"). The
 * sudo prompt component reads `isAvailable()` to decide whether to render
 * the biometric button.
 *
 * The "biometric assertion" we send to `POST /api/auth/sudo` is the
 * boolean OS-level success signal (no key-attestation Stage in v1.0).
 * Stage 2's controller currently accepts any non-empty assertion paired
 * with a recently-active session; a real WebAuthn-style attestation
 * verifier ships in a later stage.
 */
interface BiometricPluginShape {
  isAvailable(): Promise<{ isAvailable: boolean }>;
  verify(opts: { reason?: string; title?: string }): Promise<{ verified: boolean }>;
}

@Injectable({ providedIn: 'root' })
export class BiometricAuthService {
  private pluginPromise: Promise<BiometricPluginShape | null> | null = null;

  /** True when the OS reports a biometric capability the user has enrolled. */
  async isAvailable(): Promise<boolean> {
    if (!isNativePlatform()) return false;
    const plugin = await this.resolvePlugin();
    if (!plugin) return false;
    try {
      const { isAvailable } = await plugin.isAvailable();
      return !!isAvailable;
    } catch {
      return false;
    }
  }

  /**
   * Prompt the user for biometric verification. Returns an *assertion
   * payload* the caller can POST to `/api/auth/sudo` as the credential.
   *
   * Null result means: not available, not enrolled, or user declined —
   * the caller should fall back to the password field per the prompt's
   * edge-case rules.
   */
  async verifyAndGetAssertion(options: {
    reason?: string;
    title?: string;
  } = {}): Promise<string | null> {
    const plugin = await this.resolvePlugin();
    if (!plugin) return null;
    try {
      const result = await plugin.verify({
        reason: options.reason ?? 'Confirm your identity to continue.',
        title: options.title ?? 'Verify identity'
      });
      if (!result.verified) return null;
      // The server-side validator (`MobileAuthController.ValidateSudoCredentialAsync`)
      // accepts any non-empty biometric assertion in v1.0. We send a
      // discriminating prefix so server-side logs / future verifiers can
      // tell the channel apart.
      return `mob:biometric:${Date.now()}`;
    } catch {
      return null;
    }
  }

  private async resolvePlugin(): Promise<BiometricPluginShape | null> {
    if (!isNativePlatform()) return null;
    if (
      !isPluginAvailable('BiometricAuth') &&
      !isPluginAvailable('BiometricAuthNative')
    ) {
      return null;
    }
    if (!this.pluginPromise) {
      this.pluginPromise = (async () => {
        try {
          const mod = (await import(
            /* @vite-ignore */ '@capacitor-community/biometric-auth'
          )) as { BiometricAuth?: BiometricPluginShape };
          return mod.BiometricAuth ?? null;
        } catch {
          return null;
        }
      })();
    }
    return this.pluginPromise;
  }
}
